import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Header from '../components/Header';
import { 
  Trophy, Share2, Twitter, Facebook, Linkedin, Link2,
  Gift, MapPin, Calendar, CheckCircle, Download, PartyPopper,
  Camera, Sparkles, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Confetti from 'react-confetti';

const API = process.env.REACT_APP_BACKEND_URL;

const ShareWin = () => {
  const { giveawayId } = useParams();
  const { token, user } = useAuth();
  const [giveaway, setGiveaway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchGiveaway();
    // Stop confetti after 5 seconds
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, [giveawayId]);

  const fetchGiveaway = async () => {
    try {
      const response = await fetch(`${API}/api/giveaways/${giveawayId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setGiveaway(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = `${window.location.origin}/giveaways/${giveawayId}`;
  const shareText = `I just won ${giveaway?.title} worth $${giveaway?.value?.toLocaleString()} on Spinser! 🎉 Enter for FREE and you could be next!`;
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="pt-32 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const isWinner = giveaway?.winner_id === user?.user_id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-yellow-50" data-testid="share-win-page">
      <Header />
      
      {/* Confetti */}
      {showConfetti && <Confetti recycle={false} numberOfPieces={300} />}
      
      <div className="pt-32 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Winner Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 rounded-full font-bold text-lg shadow-lg">
              <Trophy className="w-6 h-6" />
              {isWinner ? "You're a Winner!" : "Winner Announcement"}
              <Sparkles className="w-5 h-5" />
            </div>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-neutral-100">
            {/* Prize Image */}
            <div className="relative h-64">
              <img 
                src={giveaway?.image_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'}
                alt={giveaway?.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
                  <MapPin className="w-4 h-4" />
                  {giveaway?.city}, {giveaway?.province_state}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                  {giveaway?.title}
                </h1>
              </div>
            </div>

            {/* Prize Details */}
            <div className="p-6 sm:p-8">
              {/* Value */}
              <div className="text-center mb-8">
                <p className="text-neutral-500 text-sm uppercase tracking-wide mb-1">Total Prize Value</p>
                <p className="text-5xl font-bold text-green-600">
                  ${giveaway?.value?.toLocaleString()}
                </p>
              </div>

              {/* Winner Info */}
              {isWinner && (
                <div className="bg-green-50 rounded-2xl p-6 mb-8 border border-green-100">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-green-800 text-lg">Congratulations, {user?.name}!</p>
                      <p className="text-green-600">Your entry was selected as the winner</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Share Section */}
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                    {isWinner ? 'Share Your Win!' : 'Share This Giveaway'}
                  </h2>
                  <p className="text-neutral-500">
                    {isWinner 
                      ? 'Let your friends know about your amazing win and help them discover Spinser!'
                      : 'Know someone who might want to enter? Share this giveaway!'}
                  </p>
                </div>

                {/* Social Share Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <a 
                    href={shareLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 p-4 bg-[#1DA1F2] text-white rounded-xl font-medium hover:bg-[#1a8cd8] transition-colors"
                    data-testid="share-twitter"
                  >
                    <Twitter className="w-5 h-5" />
                    Twitter
                  </a>
                  <a 
                    href={shareLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 p-4 bg-[#1877F2] text-white rounded-xl font-medium hover:bg-[#166fe5] transition-colors"
                    data-testid="share-facebook"
                  >
                    <Facebook className="w-5 h-5" />
                    Facebook
                  </a>
                  <a 
                    href={shareLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 p-4 bg-[#0A66C2] text-white rounded-xl font-medium hover:bg-[#095196] transition-colors"
                    data-testid="share-linkedin"
                  >
                    <Linkedin className="w-5 h-5" />
                    LinkedIn
                  </a>
                  <button 
                    onClick={copyLink}
                    className={`flex items-center justify-center gap-2 p-4 rounded-xl font-medium transition-colors ${
                      copied 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                    data-testid="share-copy-link"
                  >
                    {copied ? <CheckCircle className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>

                {/* Share Message Preview */}
                <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
                  <p className="text-sm text-neutral-600 italic">"{shareText}"</p>
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-4 text-sm text-neutral-400">or</span>
                  </div>
                </div>

                {/* Download Badge */}
                {isWinner && (
                  <Button 
                    variant="outline" 
                    className="w-full rounded-xl h-12"
                    onClick={() => toast.info('Winner badge download coming soon!')}
                    data-testid="download-badge"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Winner Badge
                  </Button>
                )}

                {/* CTA */}
                <Link to="/giveaways" className="block">
                  <Button className="w-full h-14 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl text-lg font-medium" data-testid="enter-more-giveaways">
                    <Gift className="w-5 h-5 mr-2" />
                    Enter More Giveaways
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Testimonial Prompt */}
          {isWinner && (
            <div className="mt-8 bg-gradient-to-r from-[#FF3B30] to-[#DC2626] rounded-2xl p-6 text-white text-center">
              <Camera className="w-10 h-10 mx-auto mb-3 opacity-90" />
              <h3 className="font-bold text-lg mb-2">Share Your Before & After!</h3>
              <p className="text-white/80 text-sm mb-4">
                Take photos of your project and share them with us. Your story could inspire others!
              </p>
              <Button 
                variant="secondary" 
                className="bg-white text-[#FF3B30] hover:bg-white/90"
                onClick={() => toast.info('Photo upload coming soon!')}
                data-testid="upload-photos"
              >
                Upload Photos
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareWin;
